import {Suspense, useCallback} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {TripOrderByType} from '@di-shared/api/di/type';
import {ERROR} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {HistoryDateContentProps} from '@di-shared/features/history/date/HistoryDateContent/index';
import useTripDetailEu from '@di-shared/features/history/date/useTripDetail.eu';
import {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.eu';
import useAvailableComponentEu from '@di-shared/features/home/eu/useAvailableComponent.eu';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';
import useIntersectionObserver from '@shared/useHandleObserver';

export default function ({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
          isMonthChange={isMonthChange}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate, isMonthChange}: HistoryDateContentProps) {
  const {isEmptyTrip} = useTripDetailEu({vin, selectedDate, orderBy: TripOrderByType.ASC});

  // 월 변경 로딩화면
  if (isMonthChange) {
    return <FallbackLoading />;
  }

  // No data 화면
  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  // Default 화면
  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_no_driving')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: {vin: string; selectedDate: Date}) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {checkAvailableComponent} = useAvailableComponentEu({vin});
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedValue, convertSpeedSymbol, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {tripDetailData, totalDrivingHours, totalDrivingMinutes, formattedTripDetails, fetchNextPage, hasNextPage, isFetchingNextPage} =
    useTripDetailEu({
      vin,
      selectedDate,
      orderBy: TripOrderByType.ASC,
    });

  // 새로운 주행기록 스크롤
  const handleObserver = useCallback(
    async (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const {observerRef} = useIntersectionObserver(handleObserver, {threshold: 0});

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
        <dl className="driving_summary_list">
          <div
            className="driving_summary_item"
            role="text"
            aria-label={`${t('ctn_mytrips_time')} ${
              !totalDrivingHours && !totalDrivingMinutes
                ? t('imsi_less_minute')
                : [
                    totalDrivingHours && `${totalDrivingHours}${t('ctn_mytrips_time_h_01')}`,
                    totalDrivingMinutes && `${totalDrivingMinutes}${t('ctn_mytrips_time_min_01')}`,
                  ]
                    .filter(Boolean)
                    .join(' ')
            }`}
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_time')}</dt>
            <dd className="driving_summary_info">
              {!!totalDrivingHours && (
                <>
                  <span className="driving_summary_value">{totalDrivingHours}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="driving_summary_value">{totalDrivingMinutes}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="driving_summary_value">{t('imsi_less_minute')}</span>}
            </dd>
          </div>
          <div
            className="driving_summary_item"
            role="text"
            aria-label={`${t('ctn_mytrips_distance')} ${
              isDistanceLessThan(tripDetailData.summary.totalDrvDist)
                ? t('imsi_less_distance_$unit', {
                    unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                  })
                : formatNumberWithLimitAndCommas(convertDistanceValue(tripDetailData.summary.totalDrvDist)) +
                  convertDistanceSymbol(tripDetailData.summary.totalDrvDist)
            }`}
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_distance')}</dt>
            <dd className="driving_summary_info">
              {isDistanceLessThan(tripDetailData.summary.totalDrvDist) ? (
                <span className="driving_summary_value">
                  {t('imsi_less_distance_$unit', {
                    unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                  })}
                </span>
              ) : (
                <>
                  <span className="driving_summary_value">
                    {formatNumberWithLimitAndCommas(convertDistanceValue(tripDetailData.summary.totalDrvDist))}
                  </span>
                  <span className="driving_summary_unit">{convertDistanceSymbol(tripDetailData.summary.totalDrvDist)}</span>
                </>
              )}
            </dd>
          </div>
          {checkAvailableComponent('SAFETY_SCORE') && (
            <div
              className="driving_summary_item"
              role="text"
              aria-label={`${t('ctn_mytrips_driving_safety_score')} ${formatNumberWithCommas(
                tripDetailData.summary.safetyDrvScore
              )}${t('ctn_mytrips_dss_01')} ${
                tripDetailData.summary.safetyDrvScoreDiff >= 0
                  ? `${t('ctn_mytrips_recent_rise')} ${formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}`
                  : `${t('ctn_mytrips_recent_fall')} ${formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}`
              }`}
            >
              <dt className="driving_summary_title">{t('ctn_mytrips_driving_safety_score')}</dt>
              <dd className="driving_summary_info">
                {tripDetailData.summary.safetyDrvScoreDiff >= 0 ? (
                  <span className="driving_summary_compare type_up">
                    <span className="ic ic_di_count_up" />
                    <span className="driving_summary_value">
                      {formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}
                    </span>
                  </span>
                ) : (
                  <span className="driving_summary_compare type_down">
                    <span className="ic ic_di_count_down" />
                    <span className="driving_summary_value">
                      {formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}
                    </span>
                  </span>
                )}

                <span className="driving_summary_value">{formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScore))}</span>
                <span className="driving_summary_unit">{t('ctn_home_dss_points')}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>
      {formattedTripDetails.map((trip) => {
        const ignitionOnDate = parseDateWithFormat(trip.ignOnTime);
        const ignitionOffDate = parseDateWithFormat(trip.ignOffTime);

        if (!ignitionOnDate || !ignitionOffDate) {
          throw new ExpectedError('ignitionOnDate or ignitionOffDate error');
        }

        return (
          <>
            <div
              className="trip_info type_badge"
              key={trip.ignOnTime}
            >
              <div className="trip_info_head">
                <span
                  className="trip_info_item"
                  role="text"
                  aria-label={`${DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}} ${
                    !trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes
                      ? t('imsi_less_minute')
                      : [
                          trip.intervalIgnitionHours && `${trip.intervalIgnitionHours}${t('ctn_mytrips_time_h_01')}`,
                          trip.intervalIgnitionMinutes && `${trip.intervalIgnitionMinutes}${t('ctn_mytrips_time_min_01')}`,
                        ]
                          .filter(Boolean)
                          .join(' ')
                  }`}
                >
                  {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
                </span>
                {trip?.tags
                  ?.filter(({tagType}) => tagType === 'BUSINESS')
                  .map(({tagName}) => (
                    <span
                      className="badge badge_basic_container"
                      key={tagName}
                    >
                      {tagName}
                    </span>
                  ))}
              </div>
            </div>
            <div className="statistics_record_area">
              <ul className="statistics_record_list">
                <li
                  className="statistics_record_item"
                  role="text"
                  aria-label={`${DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}} ${
                    !trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes
                      ? t('imsi_less_minute')
                      : [
                          trip.intervalIgnitionHours && `${trip.intervalIgnitionHours}${t('ctn_mytrips_time_h_01')}`,
                          trip.intervalIgnitionMinutes && `${trip.intervalIgnitionMinutes}${t('ctn_mytrips_time_min_01')}`,
                        ]
                          .filter(Boolean)
                          .join(' ')
                  }`}
                >
                  <span className="statistics_record_key">
                    <span className="ic ic_time_16px"></span>
                    {t('ctn_mytrips_time')}
                  </span>
                  <span className="statistics_record_val">
                    {!trip.intervalIgnitionHours && !trip.intervalIgnitionMinutes && (
                      <span className="statistics_record_unit">{t('imsi_less_minute')}</span>
                    )}

                    {!!trip.intervalIgnitionHours && (
                      <>
                        <span className="statistics_record_number">{trip.intervalIgnitionHours}</span>
                        <span className="statistics_record_unit">{t('ctn_mytrips_time_h_01')}</span>
                      </>
                    )}

                    {!!trip.intervalIgnitionMinutes && (
                      <>
                        <span className="statistics_record_number">{trip.intervalIgnitionMinutes}</span>
                        <span className="statistics_record_unit">{t('ctn_mytrips_time_min_01')}</span>
                      </>
                    )}
                  </span>
                </li>
                <li
                  className="statistics_record_item"
                  role="text"
                  aria-label={`${t('ctn_mytrips_distance')} ${
                    isDistanceLessThan(trip.drvDist)
                      ? t('imsi_less_distance_$unit', {
                          unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                        })
                      : formatNumberWithLimitAndCommas(convertDistanceValue(trip.drvDist)) + convertDistanceSymbol(trip.drvDist)
                  }`}
                >
                  <span className="statistics_record_key">
                    <span className="ic ic_drive_distance_16px"></span>
                    {t('ctn_mytrips_distance')}
                  </span>
                  <span className="statistics_record_val">
                    {isDistanceLessThan(trip.drvDist) ? (
                      <span className="statistics_record_unit">
                        {t('imsi_less_distance_$unit', {
                          unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                        })}
                      </span>
                    ) : (
                      <>
                        <span className="statistics_record_number">
                          {formatNumberWithLimitAndCommas(convertDistanceValue(trip.drvDist))}
                        </span>
                        <span className="statistics_record_unit">{convertDistanceSymbol(trip.drvDist)}</span>
                      </>
                    )}
                  </span>
                </li>

                <li
                  className="statistics_record_item"
                  role="text"
                  aria-label={`${t('ctn_mytrips_avg_speed')} ${formatNumberWithCommas(convertSpeedValue(trip.avgSpeed))}${convertSpeedSymbol(trip.avgSpeed)}`}
                >
                  <span className="statistics_record_key">
                    <span className="ic ic_avgspeed_16px"></span>
                    {t('ctn_mytrips_details_avg_speed')}
                  </span>
                  <span className="statistics_record_val">
                    <span className="statistics_record_number">{formatNumberWithCommas(convertSpeedValue(trip.avgSpeed))}</span>
                    <span className="statistics_record_unit">{convertSpeedSymbol(trip.avgSpeed)}</span>
                  </span>
                </li>
              </ul>
              <button
                type="button"
                className="btn_learn_more"
                onClick={() => navigate(ROUTE_PATHS.HISTORY_$TRIP_ID.getFullPath(vin, trip.id.toString()))}
              >
                {t('msg_mytrips_see_more_eu')}
                <span className="ic ic_arrow_forward_12px"></span>
              </button>
            </div>
          </>
        );
      })}
      {(isFetchingNextPage || hasNextPage) && (
        <div
          className="page_loading_box"
          ref={observerRef}
        >
          <div className="loading_lottie">
            <LottieSelector
              id="img_json_loading_black_60f"
              kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
              genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
              loop={true}
              autoplay={true}
              style={{width: 24, height: 24}}
            />
          </div>
          <span className="loading_text">{t('msg_mytrips_list_loading_eu')}</span>
        </div>
      )}
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  return (
    <div className="page_loading_box">
      <div className="loading_lottie">
        <LottieSelector
          id="img_json_loading_black_60f"
          kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
          kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
          hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
          hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
          genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
          genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
          loop={true}
          autoplay={true}
          style={{width: 24, height: 24}}
        />
      </div>
      <span className="loading_text">{t('msg_mytrips_list_loading_eu')}</span>
    </div>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();
  const {defaultDistanceSymbol} = useUnitConverter();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
        <dl className="driving_summary_list">
          <div
            className="driving_summary_item"
            role="text"
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_time')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
            </dd>
          </div>
          <div
            className="driving_summary_item"
            role="text"
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_distance')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
            </dd>
          </div>
          <div
            className="driving_summary_item"
            role="text"
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_driving_safety_score')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_home_dss_points')}</span>
            </dd>
          </div>
        </dl>
      </div>

      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_load_data')}</span>
        </div>
      </div>
    </>
  );
}
